import { apiClient as api } from './api';
import {
  StoryRequest,
  StoryResponse,
  StoryIterateRequest,
  StoryIterateResponse,
  MultiAgentStoryRequest,
  MultiAgentStoryResponse
} from '../models/story';

const STORY_SESSION_KEY = 'story_sessions';

export const storyService = {
  // Single Agent Endpoints
  async startSession(request: StoryRequest): Promise<StoryResponse> {
    // Ensure starterPrompt is not undefined or null
    if (!request.starterPrompt) {
      throw new Error('Starter prompt is required');
    }
    
    // Wrap the request in the expected structure for the backend
    const backendRequest = {
      starterPrompt: request.starterPrompt,
      request: {
        starterPrompt: request.starterPrompt, // Include in nested request too
        themes: request.themes || '',
        topics: request.topics || '',
        pointOfView: request.pointOfView || '',
        tone: request.tone || '',
        genre: request.genre || '',
        maxTokensPerIteration: request.maxTokensPerIteration || 2000,
        temperature: request.temperature || 0.8
      }
    };
    
    const response = await api.post<StoryResponse>('/api/agent/story/start', backendRequest);
    return response.data;
  },

  async iterate(sessionId: string, request: StoryIterateRequest): Promise<StoryIterateResponse> {
    // Get the existing session data to provide context
    const sessionData = this.getSessionFromLocalStorage(sessionId);
    if (!sessionData) {
      throw new Error('Session not found');
    }

    // Build the iteration request using the same endpoint but with additional context
    const iterationRequest = {
      starterPrompt: `${sessionData.starterPrompt}\n\nCurrent story so far:\n${sessionData.currentStory || sessionData.finalStory || ''}\n\nUser feedback for continuation: ${request.feedback}`,
      request: {
        starterPrompt: `${sessionData.starterPrompt}\n\nCurrent story so far:\n${sessionData.currentStory || sessionData.finalStory || ''}\n\nUser feedback for continuation: ${request.feedback}`,
        themes: sessionData.themes || '',
        topics: sessionData.topics || '',
        pointOfView: sessionData.pointOfView || '',
        tone: sessionData.tone || '',
        genre: sessionData.genre || '',
        maxTokensPerIteration: request.maxTokensPerIteration || sessionData.maxTokensPerIteration || 2000,
        temperature: request.temperature || sessionData.temperature || 0.8
      }
    };

    const response = await api.post<StoryResponse>('/api/agent/story/start', iterationRequest);
    return {
      sessionId: response.data.sessionId,
      story: response.data.finalStory || '',
      iterationNumber: (sessionData.iterations?.length || 0) + 1
    };
  },

  // Multi-Agent Endpoints
  async startMultiAgentSession(request: MultiAgentStoryRequest): Promise<MultiAgentStoryResponse> {
    const response = await api.post<MultiAgentStoryResponse>(
      '/api/multi-agent/story/start',
      request
    );
    return response.data;
  },

  // Session Management
  saveSessionToLocalStorage(sessionId: string, sessionData: any): void {
    const sessions = this.getSessionsFromLocalStorage();
    sessions[sessionId] = {
      ...sessionData,
      lastUpdated: new Date().toISOString()
    };
    localStorage.setItem(STORY_SESSION_KEY, JSON.stringify(sessions));
  },

  getSessionsFromLocalStorage(): Record<string, any> {
    const stored = localStorage.getItem(STORY_SESSION_KEY);
    return stored ? JSON.parse(stored) : {};
  },

  getSessionFromLocalStorage(sessionId: string): any {
    const sessions = this.getSessionsFromLocalStorage();
    return sessions[sessionId];
  },

  deleteSessionFromLocalStorage(sessionId: string): void {
    const sessions = this.getSessionsFromLocalStorage();
    delete sessions[sessionId];
    localStorage.setItem(STORY_SESSION_KEY, JSON.stringify(sessions));
  },

  getAllSessionIds(): string[] {
    const sessions = this.getSessionsFromLocalStorage();
    return Object.keys(sessions);
  }
};